import { useLocation } from 'react-router-dom'
import { msg, Trans } from '@lingui/macro'
import {
    Box,
    CssBaseline,
    Divider,
    Grid,
    Typography,
    useTheme,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Button
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import MobileTopNav, { MobileTopNavSpacer } from '../components/mobile/MobileTopNav'
import DesktopNav from '../components/desktop/DesktopNav'
import { useLingui } from '@lingui/react'
import DesktopToolbar from '../components/desktop/DesktopToolbar'
import SvgCustomIcon from "../components/icons/SvgCustomIcon";
import { NAV_LAYOUT_MARGIN_START } from '../consts/nav-layout-margin'
import { getCeToolUrl } from '../config/market'

export default function Help({ position, isMobile }) {
    const location = useLocation()
    const state = location.state
    const token = location.state?.token || null
    const init = location.state?.init || {}
    const { user } = init
    const theme = useTheme()
    const toolbarHeight = theme.mixins.toolbar.minHeight
    const { _ } = useLingui()

    const pdfs = [
        {
            href: '/files/ADaMS_User_Manual_FINAL_DRAFT.pdf',
            downloadName: 'ADaMS_User_Manual_EN.pdf',
            title: _(msg`User Manual`),
            subtitle: _(msg`For Crash Data System Users (English Version)`),
            img: '../assets/User_manual.png'
        },
        {
            href: '/files/ADaMS_Mobile_App_and_Data_Collection_DRAFT.pdf',
            downloadName: 'ADaMS_Mobile_App_and_Data_Collection_DRAFT.pdf',
            title: _(msg`User Manual`),
            subtitle: _(msg`Data Collection and Mobile App`),
            img: '../assets/data_collection_and_mobile_app_icon.jpg'
        },
        {
            href: '/files/Citizens_Guide_CE_Tool.pdf',
            downloadName: 'Citizens_Guide_CE_Tool.pdf',
            title: _(msg`Citizen’s Guide`),
            subtitle: _(msg`Community Engagement Tool`),
            img: '../assets/Citizens_guide_ce_tool_icon.jpg'
        }
    ]

    const downloadFile = (href, downloadName) => {
        const link = document.createElement('a')
        link.href = href
        link.download = downloadName
        link.click()
    }

    return (
        <Box component="main" sx={{ height: `calc(100vh - ${toolbarHeight}px)` }}>
            <CssBaseline />
            {isMobile ? (
                <>
                    <MobileTopNav token={token} user={user} />
                    <MobileTopNavSpacer />
                </>
            ) : (
                <>
                    <DesktopNav position={position} subPanelElements={[]} />
                    <DesktopToolbar />
                </>
            )}

            <Box
                display="flex"
                flexDirection="column"
                p={1}
                alignItems="stretch"
                sx={{
                    ...(!isMobile && { marginInlineStart: NAV_LAYOUT_MARGIN_START }),
                    '& .overload-theme--header': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText
                    }
                }}
            >

                <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.newColors.gray}` }}>
                    <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray, fontWeight: 'bold', fontSize: 24 }}>{_(msg`Help`)}</Typography>
                </Box>



                {/* --- PDF Cards --- */}
                <Box display="flex" flex={1} justifyContent="center" height="100%">
                    <Box sx={{ mt: 4, width: '95%' }}>
                        <Grid container spacing={3}>
                            {pdfs.map((pdf, i) => (
                                <Grid key={i} item xs={12} sm={6} md={3} lg={2.4}>
                                    <Card
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            borderRadius: 2,
                                            boxShadow: 2
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            image={pdf.img}
                                            alt={pdf.title}
                                            sx={{ height: 400, width: '100%', objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                                        />
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {pdf.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {pdf.subtitle}
                                            </Typography>
                                        </CardContent>
                                        <CardActions sx={{ justifyContent: 'flex-end', px: 2 }}>
                                            <Button
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                onClick={() => downloadFile(pdf.href, pdf.downloadName)}
                                            >
                                                <Typography fontSize={13}>{_(msg`Download`)}</Typography>
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {/* --- Link to CE Tool --- */}
                        <Box
                            sx={{
                                mt: 6,
                                p: 2,
                                backgroundColor: theme.palette.newColors.gray,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                width: '100%'
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center' }} onClick={() =>
                                    window.open(getCeToolUrl(), '_blank', 'noopener,noreferrer')
                                }>
                                    <SvgCustomIcon color={theme.palette.newColors.lightGray} name={'arrowCircleRight'} />
                                </Box>
                                <Typography sx={{ fontSize: 15, color: theme.palette.newColors.lightGray }}>
                                    {_(msg`Go to `)}<strong>{_(msg`Community Engagement (CE) Tool`)}</strong>
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: 14,
                                    textDecoration: 'underline',
                                    fontWeight: 700,
                                    color: theme.palette.newColors.lightGray,
                                    cursor: 'pointer',
                                }}
                                onClick={() =>
                                    window.open(getCeToolUrl(), '_blank', 'noopener,noreferrer')
                                }
                            >
                                {getCeToolUrl().replace(/^https?:\/\//, '')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
